import React, { useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import { TableLoader } from '../../components/common/Loader';
import {
  IconChevronDown, IconChevronUp, IconHistory,
} from '@tabler/icons-react';
import { useMyKPIHistory, useEmployeeKPIHistory } from '../../hooks/usePerformance';
import { useDirectReports } from '../../hooks/useEmployees';
import { useAuth } from '../../contexts/AuthContext';

export default function PerformanceHistory() {
  const { user } = useAuth();
  const orgId = user?.orgId || '';

  const [myCycleName, setMyCycleName] = useState('');
  const [myStartDate, setMyStartDate] = useState('');
  const [expandedMySection, setExpandedMySection] = useState([]);

  const { data: mySections, isLoading: loadingMySections } = useMyKPIHistory(orgId, myCycleName, myStartDate);
  const myKpiSections = Array.isArray(mySections) ? mySections : (mySections?.results || []);

  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [mgrCycleName, setMgrCycleName] = useState('');
  const [expandedEmpSection, setExpandedEmpSection] = useState([]);

  const { data: directReportsData } = useDirectReports(orgId);
  const employees = directReportsData || [];

  const { data: empSections, isLoading: loadingEmpSections } = useEmployeeKPIHistory(
    selectedEmpId || null,
    mgrCycleName,
  );
  const empKpiSections = Array.isArray(empSections) ? empSections : (empSections?.results || []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-primary/10 text-primary';
      case 'accepted': return 'bg-success/10 text-success';
      case 'rejected': return 'bg-danger/10 text-danger';
      default: return 'bg-bg-light text-text-secondary';
    }
  };

  const KpiItemRow = ({ item }) => (
    <div className="mb-10 border border-border rounded bg-bg-dark">
      <div className="grid grid-cols-12 gap-15 px-10 py-10">
        <div className="col-span-3">
          <textarea readOnly rows={3} className="w-full text-[12px] border border-border rounded p-8 resize-none bg-bg-light text-text-secondary outline-none" value={item.title || ''} />
        </div>
        <div className="col-span-1">
          <input readOnly type="number" className="w-full text-[12px] border border-border rounded p-8 text-center bg-bg-light text-text-secondary outline-none" value={Number(item.weight) ?? ''} />
        </div>
        <div className="col-span-4">
          <textarea readOnly rows={3} className="w-full text-[12px] border border-border rounded p-8 resize-none bg-bg-light text-text-secondary outline-none" value={item.description || ''} />
        </div>
        <div className="col-span-4">
          <textarea readOnly rows={3} className="w-full text-[12px] border border-border rounded p-8 resize-none bg-bg-light text-text-secondary outline-none" value={item.means_of_verification || ''} />
        </div>
      </div>

      {item.self_rating !== null && item.self_rating !== undefined && (
        <div className="flex items-center gap-20 px-10 pb-10 border-t border-border pt-10 bg-primary/5">
          <div className="flex items-center gap-8">
            <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Self Rating</label>
            <input readOnly type="number" className="text-[14px] border border-border rounded px-4 h-[38px] w-[60px] text-center bg-bg-dark text-text-primary outline-none" value={item.self_rating ?? ''} />
          </div>
          <div className="flex-1 flex items-center gap-8">
            <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Self Comment</label>
            <textarea readOnly rows={2} className="flex-1 text-[12px] border border-border rounded p-8 resize-none bg-bg-dark text-text-primary outline-none" value={item.self_comment || ''} />
          </div>
        </div>
      )}

      {item.manager_rating !== null && item.manager_rating !== undefined && (
        <div className="flex items-center gap-20 px-10 pb-10 border-t border-border pt-10 bg-success/5">
          <div className="flex items-center gap-8">
            <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Manager Rating</label>
            <input readOnly type="number" className="text-[14px] border border-border rounded px-4 h-[38px] w-[60px] text-center bg-bg-dark text-text-primary outline-none" value={item.manager_rating ?? ''} />
          </div>
          <div className="flex-1 flex items-center gap-8">
            <label className="text-[10px] font-bold text-text-secondary uppercase whitespace-nowrap">Manager Comment</label>
            <textarea readOnly rows={2} className="flex-1 text-[12px] border border-border rounded p-8 resize-none bg-bg-dark text-text-primary outline-none" value={item.manager_comment || ''} />
          </div>
        </div>
      )}
    </div>
  );

  const SectionList = ({ sections, expanded, setExpanded, loading, emptyMessage }) => {
    if (loading) return <TableLoader />;
    if (!sections || sections.length === 0) return (
      <div className="p-40 text-center text-text-secondary">
        <p className="text-sm font-medium">{emptyMessage}</p>
      </div>
    );
    return sections.map((section) => {
      const isExpanded = expanded.includes(section.id);
      return (
        <div key={section.id} className="border-b border-border last:border-b-0">
          <div className="grid grid-cols-12 px-20 py-15 items-center hover:bg-bg-light/30 transition-colors">
            <div
              className="col-span-5 flex items-center gap-10 cursor-pointer"
              onClick={() => setExpanded(prev =>
                prev.includes(section.id) ? prev.filter(i => i !== section.id) : [...prev, section.id]
              )}
            >
              {isExpanded
                ? <IconChevronUp size={14} className="text-text-secondary shrink-0" />
                : <IconChevronDown size={14} className="text-text-secondary shrink-0" />}
              <div className="px-5">
                <p className="text-[13px] font-bold text-text-primary">{section.section_name}</p>
                {section.employee_name && <p className="text-[11px] text-text-secondary mt-1">{section.employee_name}</p>}
              </div>
            </div>
            <div className="col-span-4 px-10">
              <p className="text-[12px] text-text-secondary">{section.cycle_name || '—'}</p>
              {section.cycle_start_date && <p className="text-[11px] text-text-secondary mt-1">{section.cycle_start_date}</p>}
            </div>
            <div className="col-span-3 px-10">
              <span className={`text-[11px] font-bold px-10 py-4 rounded ${getStatusColor(section.status)}`}>
                {section.status?.toUpperCase()}
              </span>
            </div>
          </div>

          {isExpanded && section.items?.length > 0 && (
            <div className="px-30 pb-20 bg-bg-light/20 border-t border-border">
              <div className="mt-15">
                <div className="grid grid-cols-12 gap-15 mb-8 px-10">
                  <div className="col-span-3"><label className="text-[10px] font-bold text-text-secondary uppercase">KPI Title</label></div>
                  <div className="col-span-1 text-center"><label className="text-[10px] font-bold text-text-secondary uppercase">Weight %</label></div>
                  <div className="col-span-4"><label className="text-[10px] font-bold text-text-secondary uppercase">Description</label></div>
                  <div className="col-span-4"><label className="text-[10px] font-bold text-text-secondary uppercase">Means Of Verification</label></div>
                </div>
                {section.items.map((item) => <KpiItemRow key={item.id} item={item} />)}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const CycleFilters = ({ cycleName, setCycleName, startDate, setStartDate }) => (
    <div className="flex flex-wrap gap-10">
      <div>
        <label className="text-[10px] font-bold text-text-secondary uppercase block mb-4">Performance Cycle</label>
        <div className="relative min-w-[200px]">
          <select
            value={cycleName}
            onChange={(e) => setCycleName(e.target.value)}
            className="w-full px-12 py-10 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 bg-bg-light text-text-primary appearance-none text-sm outline-none"
          >
            <option value="">All cycles</option>
            <option value="Annual Performance Review">Annual Performance Review</option>
            <option value="Quarterly Review">Quarterly Review</option>
          </select>
          <IconChevronDown size={16} className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-bold text-text-secondary uppercase block mb-4">Start Date</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="px-12 py-10 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 bg-bg-light text-text-primary text-sm outline-none"
        />
      </div>
    </div>
  );

  const ColumnHeaders = () => (
    <div className="grid grid-cols-12 px-20 py-10 bg-bg-light/30 text-[11px] font-bold text-text-secondary uppercase tracking-widest border-b border-border">
      <div className="col-span-5 px-10">Section</div>
      <div className="col-span-4 px-10">Cycle</div>
      <div className="col-span-3 px-10">Status</div>
    </div>
  );

  return (
    <div className="pb-40 space-y-25">
      <div className="mb-25">
        <Breadcrumb title="Performance History" path="Performance Management" />
      </div>

      {/* My KPI Sections History */}
      <div className="bg-bg-dark rounded-lg border border-border overflow-hidden shadow-light">
        <div className="px-20 py-15 border-b border-border bg-bg-light/30 flex flex-wrap items-center justify-between gap-15">
          <div>
            <h3 className="text-base font-bold text-text-primary flex items-center gap-8">
              <IconHistory size={18} className="text-primary" /> My KPI Sections History
            </h3>
            <p className="text-xs text-text-secondary mt-2">Your submitted and accepted KPI sections by cycle</p>
          </div>
          <CycleFilters
            cycleName={myCycleName} setCycleName={setMyCycleName}
            startDate={myStartDate} setStartDate={setMyStartDate}
          />
        </div>
        <ColumnHeaders />
        <div className="divide-y divide-border">
          <SectionList
            sections={myKpiSections}
            expanded={expandedMySection}
            setExpanded={setExpandedMySection}
            loading={loadingMySections}
            emptyMessage="No KPI sections found. Select a cycle above or submit your KPIs to see history here."
          />
        </div>
      </div>

      {/* Supervisee KPI History */}
      {employees.length > 0 && (
        <div className="bg-bg-dark rounded-lg border border-border overflow-hidden shadow-light">
          <div className="px-20 py-15 border-b border-border bg-bg-light/30 flex flex-wrap gap-15 items-end">
            <div>
              <label className="text-[10px] font-bold text-text-secondary uppercase block mb-4">Employee</label>
              <div className="relative min-w-[220px]">
                <select
                  value={selectedEmpId}
                  onChange={(e) => { setSelectedEmpId(e.target.value); setExpandedEmpSection([]); }}
                  className="w-full px-12 py-10 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 bg-bg-light text-text-primary appearance-none text-sm outline-none"
                >
                  <option value="">Select employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
                <IconChevronDown size={16} className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-text-secondary uppercase block mb-4">Performance Cycle</label>
              <div className="relative min-w-[200px]">
                <select
                  value={mgrCycleName}
                  onChange={(e) => setMgrCycleName(e.target.value)}
                  className="w-full px-12 py-10 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 bg-bg-light text-text-primary appearance-none text-sm outline-none"
                >
                  <option value="">All cycles</option>
                  <option value="Annual Performance Review">Annual Performance Review</option>
                  <option value="Quarterly Review">Quarterly Review</option>
                </select>
                <IconChevronDown size={16} className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
              </div>
            </div>
          </div>

          {!selectedEmpId ? (
            <div className="p-40 text-center text-text-secondary">
              <p className="text-[12px]">Select an employee to view their KPI history</p>
            </div>
          ) : (
            <>
              <ColumnHeaders />
              <div className="divide-y divide-border">
                <SectionList
                  sections={empKpiSections}
                  expanded={expandedEmpSection}
                  setExpanded={setExpandedEmpSection}
                  loading={loadingEmpSections}
                  emptyMessage={`No KPI sections found${mgrCycleName ? ` in ${mgrCycleName}` : ''}.`}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
